import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Button } from './ui/button';
import { Bell, BellOff, BellRing, Loader2, CheckCircle, AlertCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PushNotificationToggle({ compact = false }) {
  const { isAuthenticated } = useAuth();
  const { isSupported, isSubscribed, permission, subscribe, unsubscribe } = usePushNotifications(isAuthenticated);
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);

  const handleToggle = async () => {
    setLoading(true);
    try {
      if (isSubscribed) {
        await unsubscribe();
        toast.success('Notifications desactivees');
      } else {
        const ok = await subscribe();
        if (ok) {
          toast.success('Notifications activees !');
        } else if (Notification.permission === 'denied') {
          toast.error('Notifications bloquees. Activez-les dans les parametres de votre navigateur.');
        } else {
          toast.error("Impossible d'activer les notifications");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTestLoading(true);
    try {
      await axios.post(`${API}/push/test`);
      toast.success('Notification de test envoyee');
    } catch {
      toast.error("Erreur lors de l'envoi du test");
    } finally {
      setTestLoading(false);
    }
  };

  if (!isSupported) {
    if (compact) return null;
    return (
      <div className="bg-slate-50 rounded-xl p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center">
          <BellOff className="w-5 h-5 text-slate-400" />
        </div>
        <div>
          <p className="font-medium text-slate-700">Notifications non disponibles</p>
          <p className="text-sm text-slate-500">Votre navigateur ne supporte pas les notifications push</p>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <Button onClick={handleToggle} disabled={loading} variant={isSubscribed ? "default" : "outline"} size="sm"
        className={`gap-2 ${isSubscribed ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`} data-testid="push-toggle-compact">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isSubscribed ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
        {isSubscribed ? 'Notifs ON' : 'Activer'}
      </Button>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="push-notification-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isSubscribed ? 'bg-emerald-100' : 'bg-slate-100'}`}>
            {isSubscribed ? <BellRing className="w-6 h-6 text-emerald-600" /> : <Bell className="w-6 h-6 text-slate-400" />}
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Notifications push</h3>
            <p className="text-sm text-slate-500 mt-1">
              {isSubscribed
                ? 'Vous recevrez des notifications pour les nouvelles commandes et changements de statut'
                : 'Activez les notifications pour ne manquer aucune commande'}
            </p>
            {permission === 'denied' && (
              <div className="flex items-center gap-2 mt-2 text-amber-600">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">Notifications bloquees dans votre navigateur</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isSubscribed && (
            <Button onClick={handleTest} disabled={testLoading} variant="outline" size="sm" className="gap-2" data-testid="test-notification-btn">
              {testLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Tester
            </Button>
          )}
          <Button onClick={handleToggle} disabled={loading || permission === 'denied'}
            variant={isSubscribed ? "outline" : "default"}
            className={`gap-2 ${!isSubscribed ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`} data-testid="push-toggle-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isSubscribed
              ? <><CheckCircle className="w-4 h-4 text-emerald-500" />Activees</>
              : <><Bell className="w-4 h-4" />Activer</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PushNotificationToggle;
